import { Sprite, Graphics, Container } from 'pixi.js';
import { InputField } from '../components/canvas/inputfield/InputField.ts';

export enum FontFamily {
  Arial = 'Arial',
  Helvetica = 'Helvetica, sans-serif',
  TimesNewRoman = 'Times New Roman, serif',
  CourierNew = 'Courier New, monospace',
  Georgia = 'Georgia, serif',
  Verdana = 'Verdana, sans-serif',
  Tahoma = 'Tahoma, sans-serif',
  TrebuchetMS = 'Trebuchet MS, sans-serif',
  Impact = 'Impact, sans-serif',
}

export enum FontStyle {
  Normal = 'normal',
  Italic = 'italic',
  Oblique = 'oblique',
}

export enum FontWeight {
  Normal = 'normal',
  Bold = 'bold',
  Bolder = 'bolder',
  Lighter = 'lighter',
}

export enum AssetType {
  CHARACTER,
  PROP,
  BACKGROUND,
  TEXT,
  UPLOADEDASSET,
  AUDIO,
  EFFECT,
  RIGGED_CHARACTER,
  NONE,
}

export interface AnimationCharacter {
  id: string;
  animation?: any;
  position: { x: number; y: number };
  pathPoints: Position[];
  scale: number;
  name: string;
}

export interface SpriteWithId extends Sprite {
  id?: string;
}

export interface AssetCharacter {
  id: string;
  url: string;
  position: { x: number; y: number };
  scale: number;
}

export interface TextProps {
  id: string;
  text: string;
  position: { x: number; y: number };
  scale: number;
  boxWidth?: number;
  styleType?: 'default' | 'bubble';
  fontProperties: {
    fontFamily: FontFamily;
    fontSize: number;
    fontColor: string;
    fontStyle: FontStyle;
    fontWeight: FontWeight;
  };
}
export interface Position {
  x: number;
  y: number;
}

export interface MovementKeyframe {
  position: Position;
  timestamp: number;
}

export interface BaseAssetRef {
  id: string;
  position: Position;
  scale: number;
  isDragging: boolean;
  isSelected?: boolean;
  pathPoints: Position[];
  pathCircles: Graphics[];
  destroy: () => void;
}

export interface CharacterRef extends BaseAssetRef {
  sprite: Sprite;
  animation: any;
  recordedMovements?: MovementKeyframe[];
  timeline: any;
  isRecording?: boolean;
  borderGraphics: Graphics | null;
}

export interface CommentDrawingRef {
  id: string;
  pathContainer: Container;
  pathPoints: Position[] | null | undefined;
  destroy: () => void;
}

export interface TextRef extends BaseAssetRef {
  textInput: InputField;
  textBackground: Graphics;
  textContainer: Container;
  recordedMovements?: MovementKeyframe[];
  isRecording?: boolean;
}

export interface RiggedCharacterRef extends BaseAssetRef {
  rig: any; // CharacterRig from RiggingTypes
  container: Container;
  sprite: Sprite;
  boneGraphics: Graphics;
  currentAction: any | null;
  actionId?: string;
  timeline: any | null;
  startTime: number;
  // Segment-based animation properties
  borderGraphics?: Graphics | null;
  segmentSprites?: Map<string, Sprite>; // Map of boneName -> segment sprite
}

export interface Effect {
  id: string;
  type: string;
}

export interface AudioElement {
  id: string;
  url: string;
}

export interface Layer {
  id: string;
  name: string;
  type: AssetType;
  visible: boolean;
  locked: boolean;
  imageUrl?: string | null;
}

export type SpeedOption = 0.25 | 0.5 | 0.75 | 1 | 1.25 | 1.5 | 1.75 | 2;

export interface AssetRef {
  container: HTMLDivElement;
  url: string;
  isDragging: boolean;
  position: { x: number; y: number };
  scale: number;
}

export interface Comment {
  id: string;
  author: {
    name: string;
    initials: string;
    color?: string;
  };
  text: string;
  timestamp: number;
  createdAt: string;
  pathPoints?: Position[] | null;
  replies?: Comment[];
}

export interface AudioGenerationRequest {
  prompt: string;
  style: string;
  title: string;
}

export interface BackgroundGenerationRequest {
  prompt: string;
}

export interface BackgroundItem {
  id: string;
  name: string;
  imageData: any;
}

export interface Scene {
  id: string;
  name: string;
  backgroundId?: string;
  backgroundImage?: string;
  duration?: number;
}

export interface ActionData {
  src: string;
  name: string;
  ref: any;
  imageData?: any;
  sceneId?: string;
  videoData?: string;
  provider?: string;
}
